import { useState, useEffect } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import AuthTabs from "@/components/auth/AuthTabs";
import { useAuth } from "../context/AuthContext";
import { trackTwitterEvent } from '../utils/analytics';

const Login = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, session } = useAuth();
  const [searchParams] = useSearchParams();
  const defaultTab = searchParams.get('tab') === 'signup' ? 'signup' : 'login';
  
  // Get returnTo from either location state or URL search params
  const returnTo = location.state?.returnTo || searchParams.get('returnTo') || '/';
  
  useEffect(() => {
    console.log('Login Component - Initial Render', {
      user: user ? {id: user.id, email: user.email} : 'Null',
      session: session ? 'Present' : 'Null',
      returnTo: returnTo,
      currentPath: location.pathname,
      defaultTab,
      timestamp: new Date().toISOString()
    });
  }, [user, session, location, defaultTab, returnTo]);
  
  useEffect(() => {
    if (user) {
      console.log('Login Component - User Authenticated, Redirecting', {
        returnTo: returnTo,
        user: { id: user.id, email: user.email },
        timestamp: new Date().toISOString()
      });
      
      const username = user.user_metadata?.name || 
                       user.user_metadata?.full_name || 
                       user.email?.split('@')[0] || 
                       'User';
                       
      toast({
        title: "Welcome back!",
        description: `Hello, ${username}! You've been logged in successfully.`,
      });
      
      // Track successful login
      trackTwitterEvent('tw-pjw1y-pjw1y', {
        event_name: 'login_success'
      });
      
      console.log('Login Component - About to navigate to', {
        returnTo,
        timestamp: new Date().toISOString()
      });
      
      navigate(returnTo, { replace: true });
      
      console.log('Login Component - Navigation called', {
        returnTo,
        timestamp: new Date().toISOString()
      });
    }
  }, [user, navigate, returnTo, toast]);
  
  useEffect(() => {
    const handleRedirectState = async () => {
      console.log('Login Component - Checking for OAuth redirect state', {
        hash: !!window.location.hash,
        searchParams: window.location.search,
        timestamp: new Date().toISOString()
      });
      
      const { data, error } = await supabase.auth.getSession();
      
      console.log('Login Component - OAuth Redirect Check', {
        sessionPresent: !!data?.session,
        error: error ? error.message : 'No error',
        timestamp: new Date().toISOString()
      });
      
      if (data?.session) {
        const returnTo = location.state?.returnTo || '/';
        console.log('Login Component - OAuth Redirect Successful', {
          redirectTo: returnTo,
          userId: data.session.user.id,
          timestamp: new Date().toISOString()
        });
        
        window.location.href = returnTo;
        
        const displayName = data.session.user.user_metadata?.name || 
                           data.session.user.user_metadata?.full_name || 
                           data.session.user.email?.split('@')[0] || 
                           'User';
                           
        toast({
          title: "Logged in successfully",
          description: `Welcome back, ${displayName}!`,
        });
      } else if (error) {
        console.error('Login Component - Authentication Error', {
          error: error.message,
          timestamp: new Date().toISOString()
        });
        toast({
          title: "Authentication failed",
          description: error.message || "An error occurred during authentication",
          variant: "destructive"
        });
      }
    };

    if (window.location.hash || window.location.search.includes('access_token')) {
      console.log('Login Component - OAuth redirect detected in URL', {
        hash: !!window.location.hash,
        search: window.location.search,
        timestamp: new Date().toISOString()
      });
      handleRedirectState();
    }
  }, [location, navigate, toast]);

  const handleSubmit = async (e: React.FormEvent, mode: "login" | "signup") => {
    e.preventDefault();
    setIsLoading(true);
    
    console.log(`Login Component - ${mode.toUpperCase()} Attempt`, {
      email: email,
      mode: mode,
      returnTo: returnTo
    });
    
    try {
      let result;
      
      if (mode === "login") {
        result = await supabase.auth.signInWithPassword({
          email,
          password
        });
      } else {
        result = await supabase.auth.signUp({
          email,
          password
        });
      }
      
      if (result.error) {
        throw result.error;
      }
      
      console.log(`Login Component - ${mode.toUpperCase()} Success`, {
        userId: result.data.user?.id,
        sessionToken: result.data.session ? 'Present' : 'Missing',
        returnTo: returnTo
      });
      
      console.log('Login Component - Login/Signup Successful', {
        mode: mode,
        redirectTo: returnTo
      });
      
      const displayName = result.data.user?.user_metadata?.name || 
                         result.data.user?.user_metadata?.full_name || 
                         result.data.user?.email?.split('@')[0] || 
                         'User';
      
      toast({
        title: mode === "login" ? "Logged in successfully" : "Signed up successfully",
        description: mode === "login" 
          ? `Welcome back, ${displayName}!` 
          : "Your account has been created. You may need to verify your email.",
      });
      
      // Use the returnTo parameter to redirect after login success
      window.location.href = returnTo;
      
    } catch (error: any) {
      console.error('Login Component - Authentication Error', {
        mode: mode,
        errorMessage: error.message
      });
      toast({
        title: mode === "login" ? "Login failed" : "Sign up failed",
        description: error.message || "An error occurred during authentication",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSocialLogin = async (provider: 'google') => {
    console.log('Login Component - Social Login Initiated', {
      provider: provider,
      returnTo: returnTo
    });
    
    setIsLoading(true);
    
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${window.location.origin}/login`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
            returnTo: returnTo // Add returnTo as a query parameter
          }
        }
      });
      
      console.log('Login Component - OAuth Request Result', {
        provider: provider,
        success: !error,
        url: data?.url || 'No URL returned',
        error: error ? error.message : 'No error'
      });
      
      if (error) {
        console.error('Login Component - Social Login Error', {
          provider: provider,
          errorMessage: error.message
        });
        throw error;
      }
      
    } catch (error: any) {
      console.error(`Login Component - ${provider} Login Error`, {
        errorMessage: error.message
      });
      toast({
        title: `${provider.charAt(0).toUpperCase() + provider.slice(1)} login failed`,
        description: error.message || `An error occurred during ${provider} authentication`,
        variant: "destructive"
      });
      setIsLoading(false);
    }
  };

  // Log right before rendering
  const renderStatus = user ? 'authenticated-user' : 'unauthenticated-user';
  console.log('Login Component - Rendering state', {
    renderStatus,
    isLoading,
    timestamp: new Date().toISOString()
  });

  if (user) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-grow py-16 md:py-24 container">
          <div className="text-center">
            <h1 className="text-3xl font-bold mb-2">You're already logged in</h1>
            <p className="mb-4">Redirecting you to {returnTo}...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <main className="flex-grow py-16 md:py-24 container max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">Welcome to Hello ML Interview</h1>
          <p className="text-muted-foreground">
            Log in or sign up to access premium ML system design resources
          </p>
        </div>
        
        <div className="bg-card rounded-xl border shadow-sm overflow-hidden">
          <AuthTabs
            email={email}
            setEmail={setEmail}
            password={password}
            setPassword={setPassword}
            isLoading={isLoading}
            handleSubmit={handleSubmit}
            handleSocialLogin={handleSocialLogin}
            defaultTab={defaultTab}
          />
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default Login;
